import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllCampaignPosts.graphql';
import { Query, QueryallCampaignPostsArgs } from '../../__generated__/globalTypes';

export const useAllCampaignPostsQuery = (options?: QueryHookOptions<Query, QueryallCampaignPostsArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallCampaignPostsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllCampaignPostsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallCampaignPostsArgs>
): QueryResult<Query, QueryallCampaignPostsArgs> & {
  getAllCampaignPosts: LazyQueryExecFunction<Query, QueryallCampaignPostsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllCampaignPosts, result] = useLazyQuery<Query, QueryallCampaignPostsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getAllCampaignPosts, ...result };
};

export const useAllCampaignPostsPromiseQuery = () => {
  const client = useApolloClient();

  const getAllCampaignPosts = useCallback(
    (options?: Omit<QueryOptions<QueryallCampaignPostsArgs, Query>, 'query'>) =>
      client.query<Query, QueryallCampaignPostsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllCampaignPosts };
};
// Suspense Query Hooks
export const useAllCampaignPostsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryallCampaignPostsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryallCampaignPostsArgs>(QUERY, {
    ...options
  });

  return result;
};
